import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import bookingFeedbackApiService from '../../services/bookingFeedbackApi';
import './BookingFeedbackPage.css';

const BookingFeedbackPage = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();

  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!bookingId || !token) {
      setLoading(false);
      setError('This link is missing its secure access token. Open the link from your feedback email.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await bookingFeedbackApiService.getFeedbackForm(bookingId, token);
        if (cancelled) return;
        setContext(data);
        if (data.already_submitted) {
          setSubmitted(true);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Could not load feedback form.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bookingId, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!rating || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await bookingFeedbackApiService.submitFeedback(bookingId, {
        token,
        rating,
        comment,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.message || 'Could not submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const displayRating = hoverRating || rating;

  if (loading) {
    return (
      <div className="booking-feedback-page">
        <div className="booking-feedback-card">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (error && !context) {
    return (
      <div className="booking-feedback-page">
        <div className="booking-feedback-card booking-feedback-card--error">
          <h1>Feedback unavailable</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const existing = context?.feedback;
    return (
      <div className="booking-feedback-page">
        <div className="booking-feedback-card">
          <h1>Thank you</h1>
          <p>
            Your feedback for <strong>{context?.venue_name || 'the venue'}</strong> has been recorded.
          </p>
          {existing?.rating ? (
            <p className="booking-feedback-stars" aria-label={`${existing.rating} out of 5 stars`}>
              {'★'.repeat(existing.rating)}{'☆'.repeat(5 - existing.rating)}
            </p>
          ) : null}
          <p className="booking-feedback-muted">We have shared this with the venue and organizer.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-feedback-page">
      <div className="booking-feedback-card">
        <h1>How was your event?</h1>
        <p>
          Hi {context?.organizer_first_name || 'there'}, tell us about your experience at{' '}
          <strong>{context?.venue_name || 'the venue'}</strong>
          {context?.booking_reference ? (
            <>
              {' '}
              (ref. {context.booking_reference})
            </>
          ) : null}
          .
        </p>

        <form onSubmit={handleSubmit} className="booking-feedback-form">
          <fieldset className="booking-feedback-rating-field">
            <legend>Overall rating</legend>
            <div className="booking-feedback-stars-input" role="radiogroup" aria-label="Rating">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`booking-feedback-star-btn${displayRating >= value ? ' is-active' : ''}`}
                  onClick={() => setRating(value)}
                  onMouseEnter={() => setHoverRating(value)}
                  onMouseLeave={() => setHoverRating(0)}
                  aria-label={`${value} star${value === 1 ? '' : 's'}`}
                  aria-pressed={rating === value}
                >
                  ★
                </button>
              ))}
            </div>
          </fieldset>

          <label className="booking-feedback-comment-label" htmlFor="feedback-comment">
            Comments (optional)
          </label>
          <textarea
            id="feedback-comment"
            className="booking-feedback-comment"
            rows={5}
            maxLength={2000}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="What went well? Anything we should know for next time?"
          />

          {error ? (
            <p className="booking-feedback-inline-error" role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            className="booking-feedback-submit"
            disabled={!rating || submitting}
          >
            {submitting ? 'Submitting…' : 'Submit feedback'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookingFeedbackPage;
